import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAppParams, getUrlParams } from "../apiService/timespam";
import "./Theme.css";
import "./GamesForGood.css";
import { APIV4_BASE_URL } from "../apiService/apiClient";

const SPECIAL_USERS = ["5976538", "114495", "4639449"];

const GamesForGood = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme, lang } = getUrlParams();
  const { uid, goqiiUserId, nonce, apiKey, signature, goqiiAccessToken, appVersion, appType } = getAppParams();

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    if (lang === "ar") document.documentElement.dir = "rtl";
  }, [theme, lang]);

  // Prevent body-level scroll; container handles its own overflow
  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  const [view, setView] = useState("loading"); // loading | splash

  const qs = window.location.search;
  const gameMode = new URLSearchParams(qs).get("gameMode");
  const isSpecialUser = SPECIAL_USERS.includes(goqiiUserId);

  const buildBody = useCallback(
    (extra = {}) =>
      new URLSearchParams({
        goqiiUserId, nonce, apiKey, signature, goqiiAccessToken, appVersion, appType,
        userId: uid,
        ...(gameMode ? { gameMode } : {}),
        ...extra,
      }).toString(),
    [goqiiUserId, nonce, apiKey, signature, goqiiAccessToken, appVersion, appType, uid, gameMode]
  );

  const post = useCallback(
    async (endpoint, extra = {}) => {
      const res = await fetch(`${APIV4_BASE_URL}/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: buildBody(extra),
      });
      return res.json();
    },
    [buildBody]
  );

  useEffect(() => {
    const init = async () => {
      try {
        const visitJson = await post("games/determine_visit");
        if (visitJson.code === 200 && visitJson.data.visitCount >= 1) {
          if (isSpecialUser) {
            navigate(`/all-games-play${qs}`);
          } else {
            const p = new URLSearchParams(qs);
            p.set("showBanner", "1");
            navigate(`/games/${uid}?${p.toString()}`);
          }
          return;
        }
        setView("splash");
      } catch (e) {
        console.error(e);
        setView("splash");
      }
    };
    init();
  }, [post, navigate, isSpecialUser, qs, uid]);

  const handleStartTest = () => {
    navigate(isSpecialUser ? `/all-games-play${qs}` : `/games/${uid}${qs}`);
  };

  /* ---- LOADING ---- */
  if (view === "loading") {
    return (
      <div className="gfg-charts-wrapper">
        {[1, 2, 3].map((i) => <div key={i} className="gfg-shimmer" />)}
      </div>
    );
  }

  /* ---- SPLASH ---- */
  if (view === "splash") {
    return (
      <div className="gfg-splash">
        <div className="gfg-splash-bg">
          <div className="gfg-splash-content">
            <img
              src="https://storecdn.goqii.com/emailer/77204_1706858836.png"
              className="gfg-splash-brain"
              alt="brain"
            />
            <p className="gfg-splash-sub">Unleash the</p>
            <h1 className="gfg-splash-title">power of your brain!</h1>
            <p className="gfg-splash-body">
              Dive into our cognitive games and find out your cognitive age, a key to
              unlocking a smarter, more focused you.
            </p>
          </div>
        </div>
        <div className="gfg-splash-footer">
          <button className="gfg-btn-start" onClick={handleStartTest}>
            Start Test
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default GamesForGood;
